import { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { Button, TableContainer, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Grid, Divider } from "@mui/material";
import { Box } from "@mui/system";
import TableTheme from "themes/componentTheme/TableTheme";
import { IconFileTypeCsv } from "@tabler/icons-react";
import TableSearchInput from "components/ui-component/inputs/TableSearchInput";
import GridTableMain from "../GridTableMain";
import { calculateRemToPx } from "utils/utilsCommon";
import { useSelector } from "react-redux";
import TableSearchDate from "components/ui-component/inputs/TableSearchDate";
import ButtonBox from "components/ui-component/box/ButtonBox";

export const ESystemTable = ({ vtableData }) => {
    // ********************************************
    //  데이터 관리
    // ********************************************
    const [tableData, setTableData] = useState(vtableData);
    const { selectCompanyNo } = useSelector(state => state.header);
    const isMobile = window.innerWidth <= 768; // 모바일 환경 체크

    // 상세 정보 팝업 상태 관리
    const [detailOpen, setDetailOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);

    // ********************************************
    //  그리드
    // ********************************************
    const rowHeight = calculateRemToPx(2);
    const headerHeight = calculateRemToPx(2.5);
    const fixedPageSize = 50; // 페이지 크기 50으로 고정

    const gridRef = useRef();

    // 모바일용 컬럼 정의 - 사원명, 종류, 제목, 상태, 신청일만 표시
    const [columnDefs] = useState([
        {
            field: 'no', headerName: 'No',
            hide: true // AGGrid 안보이게 설정
        },
        { field: 'employeeName', headerName: '사원명', flex: 10, hide: true},
        { field: 'typeStr', headerName: '종류', flex: 20 },
        { field: 'title', headerName: '제목', flex: 40 },
        { field: 'statusStr', headerName: '상태', flex: 20 },
        { field: 'applyDate', headerName: '신청일', flex: 15, hide: true },
        // 모바일에서는 아래 컬럼 숨김
        { field: 'employeeNo', headerName: '사원번호', hide: true },
        { field: 'startDate', headerName: '시작일', hide: true },
        { field: 'endDate', headerName: '종료일', hide: true },
    ]);

    const defaultColDef = useMemo(() => {
        return {
            sortable: true,
            filter: false, // 필터 비활성화
            resizable: true,
        };
    }, []);

    const localeText = {
        // 기본 제공 텍스트를 오버라이드
        page: '',        // "페이지" 텍스트 제거
        of: '/',         // "의" 대신 "/" 사용
        to: '-',         // 필요 시 "to"도 바꿀 수 있음
        // 필요한 다른 항목들도 추가로 오버라이드 가능
      };

    const gridOptions = {
        alwaysMultiSort: true,
        rowHeight: rowHeight,
        headerHeight: headerHeight,
        pagination: true,
        paginationPageSize: fixedPageSize,
        paginationPageSizeSelector: false, // 페이지 크기 선택기 비활성화g
        domLayout: 'autoHeight',
        suppressPaginationPanel: false, // 페이지네이션 패널 표시
        localeText: localeText  // ✅ 여기 추가
    };

    // 행 클릭 이벤트 처리 함수
    const onRowClicked = useCallback((params) => {
        setSelectedRow(params.data);
        setDetailOpen(true);
    }, []);

    const onGridReady = (params) => {
        gridRef.current = params.api; // AG Grid API를 gridRef에 저장
        
        // 페이지네이션 컴포넌트 스타일 커스터마이징
        if (gridRef.current) {
            setTimeout(() => {
                const paginationPanel = document.querySelector('.ag-paging-panel');
                if (paginationPanel) {
                    const paginationElements = paginationPanel.querySelectorAll('.ag-paging-row-summary-panel span');
                    paginationElements.forEach(el => {
                        // '에서' 텍스트가 포함된 요소를 숨김
                        if (el.textContent.includes('에서')) {
                            el.style.display = 'none';
                        }
                    });
                }
            }, 100);
        }
    };

    const onBtnExport = useCallback(() => {
        if (gridRef.current) {
            // api가 정의되어 있을 때만 exportDataAsCsv를 호출
            gridRef.current.exportDataAsCsv();
        }
    }, []);

    const [searchText, setSearchText] = useState(''); // 검색어 상태 추가

    const handleSearch = (value) => {
        setSearchText(value);
    }

    // 상세 정보 팝업 닫기 함수
    const handleCloseDetail = () => {
        setDetailOpen(false);
    };

    useEffect(() => {
        setTableData(vtableData);
    }, [vtableData]);

    // 데이터가 변경될 때마다 페이지네이션 스타일 업데이트
    useEffect(() => {
        if (gridRef.current) {
            setTimeout(() => {
                const paginationPanel = document.querySelector('.ag-paging-panel');
                if (paginationPanel) {
                  // "에서" 포함 텍스트 제거
                    paginationPanel.querySelectorAll('span').forEach(el => {
                        if (el.textContent.includes('에서')) {
                            el.style.display = 'none';
                        }
                    });
                }
            }, 100);
        }
    }, [tableData]);

    const [searchDateStr, setSearchDateStr] = useState('');
    const [searchDateEnd, setSearchDateEnd] = useState('');

    const handleDateStrChange = (date) => {
        setSearchDateStr(date);
    }

    const handleDateEndChange = (date) => {
        setSearchDateEnd(date);
    }

    // 모바일 스타일 적용
    const mobileStyles = {
        tableContainer: {
            padding: '0',
            width: '100%',
            overflowX: 'hidden'
        },
        buttonBox: {
            display: 'flex', 
            justifyContent: 'space-between', 
            padding: '0.5rem 0',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? '8px' : '0'
        },
        gridContainer: { 
            height: '100%', 
            width: '100%',
            fontSize: isMobile ? '0.85rem' : '1rem' 
        },
        detailDialog: {
            padding: '1rem'
        },
        detailItem: {
            marginBottom: '0.5rem'
        }
    };

    // 상세 정보 필드와 라벨 정의
    const detailFields = [
        { field: 'employeeName', label: '사원명' },
        { field: 'employeeNo', label: '사원번호' },
        { field: 'typeStr', label: '종류' },
        { field: 'title', label: '제목' },
        { field: 'statusStr', label: '상태' },
        { field: 'applyDate', label: '신청일' },
        { field: 'startDate', label: '시작일' },
        { field: 'endDate', label: '종료일' }
    ];

    return (
        <TableTheme>
            <TableContainer id='printableArea' sx={mobileStyles.tableContainer}>
                <Box sx={mobileStyles.buttonBox}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Button variant="outlined" size="small" onClick={onBtnExport}>
                            <IconFileTypeCsv />
                            <Typography variant="subtitle" sx={{ padding: '0 0.25rem' }}></Typography>
                        </Button>
                    </Box>
                    <ButtonBox>
                        
                        <TableSearchDate onDateChange={handleDateStrChange} label='시작일' />
                        <Typography variant="subtitle" >~</Typography>
                        <TableSearchDate onDateChange={handleDateEndChange} label='종료일' />
                    </ButtonBox>
                    <Box>
                        <TableSearchInput onSearch={handleSearch}  />
                    </Box>
                </Box>
                <GridTableMain
                    box_style={mobileStyles.gridContainer}
                    style={{ textAlign: 'center' }}
                    onGridReady={onGridReady}
                    onRowClicked={onRowClicked} // 행 클릭 이벤트 추가
                    ref={gridRef}
                    defaultColDef={defaultColDef}
                    rowData={tableData}
                    columnDefs={columnDefs}
                    gridOptions={gridOptions}
                    quickFilterText={searchText}
                />

                {/* 상세 정보 팝업 */}
                <Dialog 
                    open={detailOpen} 
                    onClose={handleCloseDetail}
                    fullWidth
                    maxWidth="sm"
                >
                    <DialogTitle>상세 정보</DialogTitle>
                    <DialogContent dividers sx={mobileStyles.detailDialog}>
                        {selectedRow && (
                            <Grid container spacing={2}>
                                {detailFields.map((field, index) => (
                                    <Grid item xs={12} key={index} sx={mobileStyles.detailItem}>
                                        <Grid container>
                                            <Grid item xs={4}>
                                                <Typography variant="body1" fontWeight="bold">
                                                    {field.label}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={8}>
                                                <Typography variant="body1">
                                                    {selectedRow[field.field] || '-'}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                        {index < detailFields.length - 1 && <Divider sx={{ mt: 1 }} />}
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDetail} variant="contained" color="primary">
                            닫기
                        </Button>
                    </DialogActions>
                </Dialog>
            </TableContainer>
        </TableTheme>
    );
};

export default ESystemTable;